import { SavedConversation } from '../types';

/**
 * Service pour gérer les conversations avec le backend MySQL
 */
export class ConversationService {
  /**
   * Sauvegarder une conversation sur le serveur
   */
  static async saveConversation(conversation: SavedConversation): Promise<{ 
    success: boolean; 
    id?: string; 
    error?: string;
    action?: 'created' | 'updated';
  }> {
    try {
      const response = await fetch('/save-conversation.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ conversation }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur inconnue du serveur');
      }
      
      return data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la conversation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Récupérer la liste de toutes les conversations (métadonnées uniquement)
   */
  static async listConversations(limit: number = 100, offset: number = 0): Promise<{
    success: boolean;
    conversations?: SavedConversation[];
    total?: number;
    error?: string;
  }> {
    try {
      const response = await fetch(`/list-conversations.php?limit=${limit}&offset=${offset}`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur inconnue du serveur');
      }

      // Convertir les dates ISO en objets Date
      if (data.conversations) {
        data.conversations = data.conversations.map((conv: any) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          lastModified: new Date(conv.lastModified)
        }));
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des conversations:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Récupérer une conversation complète avec tous les messages
   */
  static async getConversation(id: string): Promise<{
    success: boolean;
    conversation?: SavedConversation;
    error?: string;
  }> {
    try {
      const response = await fetch(`/get-conversation.php?id=${encodeURIComponent(id)}`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur inconnue du serveur');
      }

      // Convertir les dates ISO en objets Date
      if (data.conversation) {
        data.conversation.createdAt = new Date(data.conversation.createdAt);
        data.conversation.lastModified = new Date(data.conversation.lastModified);
        
        // Convertir les timestamps des messages
        if (data.conversation.messages) {
          data.conversation.messages = data.conversation.messages.map((msg: any) => ({
            ...msg,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
          }));
        }
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la conversation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Supprimer une conversation
   */
  static async deleteConversation(id: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch('/delete-conversation.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur inconnue du serveur');
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la conversation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Tester la connexion à la base de données
   */
  static async testConnection(): Promise<{
    success: boolean;
    message?: string;
    conversations_count?: number;
    error?: string;
  }> {
    try {
      const response = await fetch('/test-db.php');

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors du test de connexion:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }
}


